import React from 'react';
import { TemplateType } from '../TemplateType';
import { useTranslation } from 'react-i18next';
import usePdfContent, { PdfContentParams } from '../usePdfContent';
import { useLocation } from 'react-router-dom';
import { makePath } from 'apps/Pdf/helpers/helpers';

import Grid from '@material-ui/core/Grid';

import { pluralize } from 'helpers/helpers';
import PdfContent from 'apps/Pdf/components/common/PdfContent';
import Title from 'apps/Pdf/components/common/Title';
import BasicData from 'apps/Pdf/components/BasicData/BasicData';
import Disclaimer from 'apps/Pdf/components/Disclaimer/Disclaimer';
import H1 from 'apps/Pdf/components/common/H1';
import H2 from 'apps/Pdf/components/common/H2';
import P from 'apps/Pdf/components/common/P';
import PageBreak from 'apps/Pdf/components/common/PageBreak';
import Signature from 'apps/Pdf/components/Signature/Signature';
import EvaluationCategory from './components/EvaluationCategory';
import QuestionsAndAnswers from './components/QuestionsAndAnswers';
import Loading from 'apps/Pdf/components/common/Loading';

import useGeneralStyles from 'apps/Pdf/styles/general';
import useColorStyles from 'apps/Pdf/styles/colors';
import { InvestmentQuestionnaireContent } from './InvestmentQuestionnaireInterface';
import { Currency } from 'enums';
import { PdfCategory } from '../Interfaces';

const InvestmentQuestionnaire = (params: PdfContentParams) => {
  const { t } = useTranslation();
  const styles = useGeneralStyles();
  const location = useLocation();
  const newQuest = makePath(TPL_NEW_INVESTMENT_QUESTIONNAIRE) === location.pathname.slice(0, -1);

  const pdfContent = usePdfContent<InvestmentQuestionnaireContent>({
    ...params,
    category: PdfCategory.SIGNATURE,
    templateType: newQuest ? TPL_NEW_INVESTMENT_QUESTIONNAIRE : TPL_INVESTMENT_QUESTIONNAIRE,
  });

  const data = pdfContent?.data ? pdfContent.data : ({} as InvestmentQuestionnaireContent);
  const colors = useColorStyles({ code: pdfContent?.data?.header.signedVia || '' });
  const currency = data ? data.currency : Currency.CZK;

  return pdfContent ? (
    <PdfContent errors={pdfContent?.errors} className={colors.root} onload afterLoad={params.afterLoad}>
      <Title>Investiční dotazník a vyhodnocení</Title>

      <BasicData data={pdfContent.data!.header} plan />

      {data.clientData?.representative && (
        <>
          <H2>Zástupce zákazníka</H2>
          <div className={`${styles.twoColumns} ${styles.lines}`}>
            <Grid container>
              <Grid container item xs={4}>
                Jméno:
              </Grid>
              <Grid container item xs={8} className={`${styles.value}`}>
                {data.clientData.representative.firstname} {data.clientData.representative.lastname}
              </Grid>
              <Grid container item xs={4}>
                Adresa:
              </Grid>
              <Grid container item xs={8} className={`${styles.value}`}>
                {data.clientData.representative.permanentAddressCompact},{' '}
                {t(`country_${data.clientData.representative.permanentAddress.country}`)}
              </Grid>
            </Grid>
            <Grid container>
              <Grid container item xs={4}>
                Rodné číslo:
              </Grid>
              <Grid container item xs={8} className={`${styles.value}`}>
                {data.clientData.representative.foreignId || data.clientData.representative.personalNumber}
              </Grid>
              <Grid container item xs={4}>
                Email:
              </Grid>
              <Grid container item xs={8} className={`${styles.value}`}>
                {data.clientData.representative.email}
              </Grid>
              <Grid container item xs={4}>
                Telefon:
              </Grid>
              <Grid container item xs={8} className={`${styles.value}`}>
                {data.clientData.representative.phone}
              </Grid>
            </Grid>
          </div>
        </>
      )}

      <H1>Vyhodnocení testu vhodnosti</H1>

      <EvaluationCategory
        total={true}
        category="Výsledný rizikový profil zákazníka:"
        minValueLabel={t('investmentQuestionnaire_conservative')}
        maxValueLabel={t('investmentQuestionnaire_aggressive')}
        risk={data.investmentQuestionnaireResult.profile}
      />

      <EvaluationCategory
        category={t('investmentQuestionnaire_riskTolerance')}
        minValueLabel={t('investmentQuestionnaire_careful')}
        maxValueLabel={t('investmentQuestionnaire_brave')}
        risk={data.investmentQuestionnaireResult.categoryProfiles.RISK_TOLERANCE}
      />

      <EvaluationCategory
        category={t(
          data.investmentQuestionnaireResult.categoryProfiles.KNOWLEDGE
            ? 'investmentQuestionnaire_experience'
            : 'investmentQuestionnaire_expAndKnowledge',
        )}
        minValueLabel={t('investmentQuestionnaire_expLow')}
        maxValueLabel={t('investmentQuestionnaire_expHigh')}
        risk={data.investmentQuestionnaireResult.categoryProfiles.EXPERIENCE}
      />

      {data.investmentQuestionnaireResult.categoryProfiles.KNOWLEDGE && (
        <EvaluationCategory
          risk={data.investmentQuestionnaireResult.categoryProfiles.KNOWLEDGE}
          category={t('investmentQuestionnaire_knowledge')}
          minValueLabel={t('investmentQuestionnaire_expLow')}
          maxValueLabel={t('investmentQuestionnaire_expHigh')}
        />
      )}

      <EvaluationCategory
        category={t('investmentQuestionnaire_background')}
        minValueLabel={t('investmentQuestionnaire_unstable')}
        maxValueLabel={t('investmentQuestionnaire_stable')}
        risk={data.investmentQuestionnaireResult.categoryProfiles.BACKGROUND}
      />

      <EvaluationCategory
        category={t('investmentQuestionnaire_horizon')}
        minValueLabel={t('investmentQuestionnaire_shortTerm')}
        maxValueLabel={t('investmentQuestionnaire_longTerm')}
        risk={data.investmentQuestionnaireResult.categoryProfiles.HORIZON}
      />

      {data.version > 1 && (
        <EvaluationCategory
          category={t('investmentQuestionnaire_esg_title')}
          esg={t(`investmentQuestionnaire_esg_${data.investmentQuestionnaireQuestions.answers[21] === '1'}`)}
        />
      )}

      <P className="supplement">
        Váš výsledný rizikový profil určuje přednastavenou investiční strategii aplikace Edward. V případě, že jste
        definovali částky, které chcete v budoucnu vybrat na své finanční cíle, bude Edward Váš majetek rozdělovat mezi
        dlouhodobé, střednědobé a krátkodobé portfolio a pro každé portfolio budete mít nastavenou investiční strategii
        s jinou rizikovostí. Pro dlouhodobé portfolio při tom může aplikace Edward přednastavit i rizikovější investiční
        strategii, než je Váš výsledný rizikový profil.
      </P>

      <PageBreak />

      <H1>Vyhodnocení cílového trhu</H1>

      <Grid container className={`${styles.bigLine} ${styles.regularText}`}>
        <Grid item xs={4}>
          Kategorie:
        </Grid>
        <Grid item xs={8}>
          Neprofesionální investor
        </Grid>
      </Grid>
      <Grid container className={`${styles.bigLine} ${styles.regularText}`}>
        <Grid item xs={4}>
          Znalosti a zkušenosti:
        </Grid>
        <Grid item xs={8}>
          {t([
            `accountInvestment_category_exp_${data.investmentQuestionnaireResult.experience}`,
            `accountInvestment_category_${data.investmentQuestionnaireResult.experience}`,
          ])}
        </Grid>
      </Grid>
      <Grid container className={`${styles.bigLine} ${styles.regularText}`}>
        <Grid item xs={4}>
          Investiční horizont:
        </Grid>
        <Grid item xs={8}>
          {Object.keys(data.investmentQuestionnaireResult.horizon).length === 1 ? (
            <>
              {t(`bucket.default.title`)}: {data.investmentQuestionnaireResult.horizon['LONG_TERM']}{' '}
              {pluralize(
                data.investmentQuestionnaireResult.horizon['LONG_TERM'],
                t('general_year'),
                t('general_years_234'),
                t('general_years'),
              )}
            </>
          ) : (
            Object.keys(data.investmentQuestionnaireResult.horizon).map((key) => (
              <>
                {t(`bucket.${key.toLowerCase()}.title`)}: {data.investmentQuestionnaireResult.horizon[key]}{' '}
                {pluralize(
                  data.investmentQuestionnaireResult.horizon[key],
                  t('general_year'),
                  t('general_years_234'),
                  t('general_years'),
                )}
                <br />
              </>
            ))
          )}
        </Grid>
      </Grid>
      <Grid container className={`${styles.bigLine} ${styles.regularText}`}>
        <Grid item xs={4}>
          Schopnost nést ztráty:
        </Grid>
        <Grid item xs={8}>
          {data.investmentQuestionnaireResult.lossTolerance === true ? 'ANO' : 'NE'}
        </Grid>
      </Grid>
      <Grid container className={`${styles.bigLine} ${styles.regularText}`}>
        <Grid item xs={4}>
          Investiční cíle:
        </Grid>
        <Grid item xs={8}>
          {t(`accountInvestment_target_${data.investmentQuestionnaireResult.target}`)}
        </Grid>
      </Grid>
      <Grid container className={`${styles.bigLine} ${styles.regularText}`}>
        <Grid item xs={4}>
          Tolerance rizika (max. SRRI):
        </Grid>
        <Grid item xs={8}>
          {data.investmentQuestionnaireResult.riskToleranceIndex}
        </Grid>
      </Grid>

      {pdfContent?.data?.header.signedVia !== 'BT' && (
        <>
          <H1>Vyhodnocení testu přiměřenosti</H1>
          <Grid container className={`${styles.bigLine} ${styles.regularText}`}>
            <Grid item xs={4}>
              Výsledná kategorie investora:
            </Grid>
            <Grid item xs={8}>
              {t(`accountInvestment_category_${data.investmentQuestionnaireResult.experience}`)}
            </Grid>
          </Grid>
        </>
      )}

      <PageBreak />

      <H1>Odpovědi na otázky</H1>

      <QuestionsAndAnswers
        data={data.investmentQuestionnaireQuestions.answers}
        reasons={data.investmentQuestionnaireReasons.reasons}
        version={data.version.toString(10)}
        currency={currency}
      />

      <PageBreak />

      <H1>Prohlášení zákazníka a informace o způsobu vyplnění investičního dotazníku</H1>
      <P>
        Zákazník prohlašuje, že dotazník vyplnil dobrovolně, že poskytnuté údaje jsou aktuální, přesné a úplné a
        zavazuje se informovat {data.header.intermediaryCompany.companyName} o případných změnách.
      </P>

      <P>
        Zákazník si je zároveň vědom toho, že uvedení neúplných nebo nepravdivých údajů ho vystavuje riziku realizace
        nevhodných obchodů nebo výběr nevhodné investiční strategie.
      </P>

      <P>Zákazník souhlasí s výsledkem „investičního dotazníku“, který odpovídá jeho rizikovému profilu.</P>

      <span className={styles.regularText}>Zákazník potvrzuje, že byl seznámen a převzal:</span>
      <br />
      <ul className={styles.regularText}>
        <li>{t(`pdf_document_docs`)}</li>
        <li>
          Informace týkající se zpracování osobních údajů jsou umístěny na{' '}
          <a href={data.header.intermediaryCompany.dataProtectionUrl} target="_blank" rel="noopener noreferrer">
            {data.header.intermediaryCompany.dataProtectionUrl}
          </a>
          .
        </li>
      </ul>

      <H1>Způsob vyplnění investičního dotazníku</H1>
      <P>
        Výše uvedený investiční dotazník byl vyplněn on-line v rámci aplikace Edward. Odpovědi, které zákazník poskytl
        poradci, byly zaznamenány pravdivě a na jejich základě bylo zákazníkovi prezentováno vyhodnocení investičního
        dotazníku přímo na schůzce s poradcem. Kopie tohoto vyhodnocení byla zákazníkovi zaslána na výše uvedený email.
      </P>

      <H1>Kategorizace zákazníka</H1>
      <P>
        Kategorizace zákazníka - výše uvedený zákazník je zařazen do kategorie požívající nejvyšší míru ochrany
        (neprofesionální zákazník) dle směrnice Evropského parlamentu a Rady 2004/39/ES ze dne 21. dubna 2004 o trzích
        finančních nástrojů (MiFID), ve znění směrnice Evropského parlamentu a Rady 2006/31/ES a 2007/44/ES (MiFID).
      </P>

      <Disclaimer />

      <Signature protocol={data.signedInfo} />
    </PdfContent>
  ) : params.preview ? (
    <Loading />
  ) : null;
};

const TPL_INVESTMENT_QUESTIONNAIRE: TemplateType = 'investmentQuestionnaire';
const TPL_NEW_INVESTMENT_QUESTIONNAIRE: TemplateType = 'newInvestmentQuestionnaire';

export { TPL_INVESTMENT_QUESTIONNAIRE, TPL_NEW_INVESTMENT_QUESTIONNAIRE, InvestmentQuestionnaire };
